import { ref, computed, watch } from 'vue';

/**
 * Composable for inline contact name autocomplete after "Hello"
 * @param {Object} contact - The contact object with name property
 * @param {Function} getEditorText - Function that returns current editor text content
 * @returns {Object} - Autocomplete state and methods
 */
export function useContactNameAutocomplete(contact, getEditorText) {
  const suggestion = ref('');
  const showSuggestion = ref(false);

  const contactFirstName = computed(() => {
    if (!contact?.value?.name) return '';
    const name = contact.value.name.trim();
    const firstName = name.split(/\s+/)[0];
    return firstName || '';
  });

  /**
   * Check if text ends with "Hello" (case-insensitive) and suggest contact name
   * @param {string} text - Current editor text
   */
  const checkForHelloPattern = text => {
    if (!text || !contactFirstName.value) {
      suggestion.value = '';
      showSuggestion.value = false;
      return;
    }

    // Check if text ends with "hello" (with or without trailing space)
    const helloWithoutSpace = /\bhello$/i.test(text);
    const helloWithSpace = /\bhello\s$/i.test(text);

    if ((helloWithoutSpace || helloWithSpace) && contactFirstName.value) {
      // If "hello" has no trailing space, add space before name
      // If "hello " has trailing space, don't add extra space
      const prefix = helloWithoutSpace ? ' ' : '';
      suggestion.value = `${prefix}${contactFirstName.value},`;
      showSuggestion.value = true;
    } else {
      suggestion.value = '';
      showSuggestion.value = false;
    }
  };

  /**
   * Accept the suggestion and return the text to insert
   * @returns {string} - The suggestion text to insert
   */
  const acceptSuggestion = () => {
    if (!showSuggestion.value || !suggestion.value) return '';
    const accepted = suggestion.value;
    suggestion.value = '';
    showSuggestion.value = false;
    return accepted;
  };

  /**
   * Clear the current suggestion
   */
  const clearSuggestion = () => {
    suggestion.value = '';
    showSuggestion.value = false;
  };

  /**
   * Update autocomplete based on current editor content
   * @param {string} text - Current editor text
   */
  const updateAutocomplete = text => {
    checkForHelloPattern(text);
  };

  // Watch contact changes to clear suggestions if contact changes
  watch(
    () => contact?.value?.id,
    () => {
      clearSuggestion();
    }
  );

  return {
    suggestion,
    showSuggestion,
    contactFirstName,
    updateAutocomplete,
    acceptSuggestion,
    clearSuggestion,
  };
}
